/* eslint-disable no-case-declarations */
import SpatialAdjustEntityType = ToroEnums.SpatialAdjustEntityType;

import { IMqttMessage, MqttConnectionState, MqttService } from 'ngx-mqtt';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthManagerService } from '../auth/auth-manager.service';
import { BroadcastService } from '../../common/services/broadcast.service';
import { CloudServiceBase } from './cloud-service-base';
import { CorrelationService } from '../../common/services/correlation.service';
import { GenericMqttMessage } from './models/generic-mqtt-message.model';
import { GetCourseReportMessage } from './models/mqtt-get-course-report/get-course-report-message.model';
import { GetCoursesMessage } from './models/mqtt-get-courses/get-courses-message.model';
import { GetGeoTopoMessage } from './models/mqtt-get-geo-topo/get-geo-topo-message.model';
import { GetProgramTopoMessage } from './models/mqtt-get-program-topo/get-program-topo-message.model';
import { GetStationsMessage } from './models/mqtt-get-stations/get-stations-message.model';
import { ILynxProxyService } from '../lynx-proxy/ILynxProxyService';
import { Injectable } from '@angular/core';
import { LoggerService } from '../../common/services/logger.service';
import { LynxCloudApiService } from './lynx-cloud-api.service';
import { LynxCloudCourseItem } from '../../core/dashboard/widgets/lynx/widget-lynx-cloud/models/lynx-cloud-course-item.model';
import { LynxPercentAdjust } from './models/mqtt-percent-adjust/lynx-percent-adjust.model';
import { PingResponse } from './models/mqtt-ping/ping-response.model';
import { ToroEnums } from '../../common/enumerations/toro.enums';
import { TranslateService } from '@ngx-translate/core';
import { v4 as uuidv4 } from 'uuid';

@UntilDestroy()
@Injectable({
    providedIn: 'root'
})
export class LynxCloudService extends CloudServiceBase implements ILynxProxyService {
    // Member Vars
    private readonly genericMessageId = '7da2fdb1-9c03-4982-cd52-ba3b65f45d99';
    private readonly clientId: string;
    private isPendingInitialization = false;

    // private demoModeMockDataService: DemoModeMockDataService;

    // =========================================================================================================================================================
    // Ctor
    // =========================================================================================================================================================

    constructor(protected authManager: AuthManagerService,
                protected broadcastService: BroadcastService,
                protected correlationService: CorrelationService,
                private lynxCloudApiService: LynxCloudApiService,
                private mqttService: MqttService,
                protected logger: LoggerService,
                protected translate: TranslateService
    ) {
        super(authManager, broadcastService, correlationService, logger, translate);

        this.initMqttBroker();

        this.clientId = `client-${uuidv4()}`
    }

    // =========================================================================================================================================================
    // public Methods
    // =========================================================================================================================================================

    public initMqttBroker() {
        if (this.isPendingInitialization || this.isInitialized) return;

        this.isPendingInitialization = true;

        this.lynxCloudApiService.init()
            .subscribe({
                next: result => {
                    const bits = result.split('|#|');
                    this.mqttService.connect({ username: bits[0], password: bits[1] });
                    this.setupMqttListeners();
                },
                error: err => {
                    setTimeout(() => this.initMqttBroker(), 15000)
                }
            });
    }

    public requestChangePercentAdjust(entityType: SpatialAdjustEntityType = SpatialAdjustEntityType.ProgramStation, percentAdjust: number, ids: number[]): void {
        if (this.spatialAdjustCourseItem == null) throw new Error("LynxCloud course information is not set.");

        const message = `subject: change-pct-adjust\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n\t"Ids": [${ids}],\n"EntityType": ${entityType},\n"PercentAdjust": ${percentAdjust}\n}`
        const commandTopic = `${this.spatialAdjustCourseItem.installationId}/server/command`;

        this.mqttService.publish(commandTopic, message)
            .subscribe({
                next: _ => {
                    /** No action */
                },
                error: err => setTimeout(() => {
                    this.requestChangePercentAdjust(entityType, percentAdjust, ids);
                }, 5000)
            })
    }

    // =========================================================================================================================================================
    // Base class Overrides
    // =========================================================================================================================================================

    protected override requestPingLynx(courseItem: LynxCloudCourseItem): void {
        const message = `subject: ping\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n}`
        const commandTopic = `${courseItem.installationId}/server/command`;

        this.mqttService.publish(commandTopic, message)
            .subscribe({
                next: _ => { },
                error: err => setTimeout(() => this.requestPingLynx(courseItem), 5000)
            })
    }

    protected override requestCoursesInternal() {
        this.requestCoursesList();
    }

    protected override requestStations(courseItem: LynxCloudCourseItem): void {
        const message = `subject: get-stations\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n\t"Id": ${courseItem.courseId}\n}`
        const commandTopic = `${courseItem.installationId}/server/command`;

        this.mqttService.publish(commandTopic, message)
            .subscribe({
                next: _ => { },
                error: err => setTimeout(() => this.requestStations(courseItem), 5000)
            })
    }

    protected override requestProgramTopo(courseItem: LynxCloudCourseItem): void {
        const message = `subject: get-program-topo\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n\t"Id": ${courseItem.courseId}\n}`
        const commandTopic = `${courseItem.installationId}/server/command`;

        this.mqttService.publish(commandTopic, message)
            .subscribe({
                next: _ => { },
                error: err => setTimeout(() => this.requestProgramTopo(courseItem), 5000)
            })
    }

    protected override requestPercentAdjustments(courseItem: LynxCloudCourseItem): void {
        const message = `subject: get-percent-adjustments\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n\t"Id": ${courseItem.courseId}\n}`
        const commandTopic = `${courseItem.installationId}/server/command`;

        this.mqttService.publish(commandTopic, message)
            .subscribe({
                next: _ => { },
                error: err => setTimeout(() => {
                    this.requestPercentAdjustments(courseItem)
                }, 5000)
            })
    }

    protected override requestGeoTopography(courseItem: LynxCloudCourseItem): void {
        const message = `subject: get-geo-topo\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n\t"Id": ${courseItem.courseId}\n}`
        const commandTopic = `${courseItem.installationId}/server/command`;

        this.mqttService.publish(commandTopic, message)
            .subscribe({
                next: _ => { },
                error: err => setTimeout(() => this.requestGeoTopography(courseItem), 5000)
            })
    }

    protected override requestCourseReport(courseItem: LynxCloudCourseItem): void {
        const message = `subject: get-course-report\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n\t"Id": ${courseItem.courseId}\n}`
        const commandTopic = `${courseItem.installationId}/server/command`;

        this.mqttService.publish(commandTopic, message)
            .subscribe({
                next: _ => { },
                error: err => setTimeout(() => this.requestCourseReport(courseItem), 5000)
            })
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private requestCoursesList(): void {
        const message = `subject: get-courses\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n}`

        this.installationIds.forEach(id => {
            const commandTopic = `${id}/server/command`;

            this.mqttService.publish(commandTopic, message)
                .subscribe({
                    next: _ => {
                        /** No action */
                    },
                    error: err => setTimeout(() => {
                        this.requestCoursesList()
                    }, 5000)
                })
        })
    }

    // private requestChangePercentAdjustDuration(courseItem: LynxCloudCourseItem): void {
    //     const message = `subject: change-pct-adjust-duration\nmessage-id: ${this.genericMessageId}\nclient-id: ${this.clientId}\ncontent-type: application/json\ncontent:\n{\n\t"Id": ${courseItem.courseId}\n}`
    //     const commandTopic = `${courseItem.installationId}/server/command`;
    //
    //     this.mqttService.publish(commandTopic, message)
    //         .subscribe({
    //             next: _ => { },
    //             error: err => setTimeout(() => {
    //                 this.requestChangePercentAdjustDuration(courseItem)
    //             }, 5000)
    //         })
    // }

    private setupMqttListeners() {
        this.mqttService.state
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (state: MqttConnectionState) => {
                    switch (state) {
                        case MqttConnectionState.CONNECTING:
                            break;
                        case MqttConnectionState.CONNECTED:
                            this.isPendingInitialization = false;
                            this.isInitialized = true;
                            this.mqttBrokerConnected();
                            break;
                        case MqttConnectionState.CLOSED:
                            break;
                    }
                }
            })
    }

    private mqttBrokerConnected() {
        this.installationIds.forEach(id => {

            const responseTopic = `${id}/server/response/${this.clientId}`;

            this.mqttService.observe(responseTopic)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (message: IMqttMessage) => {
                        const payload = this.parseGenericMessage(message.payload.toString())

                        switch (payload.subject) {
                            case 'ping':
                                const response = new PingResponse(payload);
                                this.pingResponseReceived.next(response);
                                break;
                            case 'get-course-report':
                                const courseReport = new GetCourseReportMessage(payload);
                                this.courseReportRetrieved.next(courseReport.content.result)
                                break;
                            case 'get-courses':
                                const installationId = message.topic.split('/')[0];
                                const courses = new GetCoursesMessage({ ...payload, installationId });
                                this.courseListRetrieved.next(courses.content.result);
                                break;
                            case 'get-stations':
                                const stations = new GetStationsMessage(payload);
                                this.lynxStations = stations.content.result;
                                this.stationsRetrieved.next(stations.content.result);
                                break;
                            case 'get-hardware-topo':
                                break;
                            case 'get-geo-topo':
                                const topography = new GetGeoTopoMessage(payload);
                                this.geoTopographyRetrieved.next(topography.content.result)
                                break;
                            case 'get-program-topo':
                                const topo = new GetProgramTopoMessage(payload);
                                this.lynxPrograms = topo.content.result;
                                this.programTopoRetrieved.next(topo.content.result);
                                break;
                            case 'get-switches':
                                break;
                            case 'get-percent-adjustments':
                                this.lynxPercentAdjustments = payload.content.result.map(r => new LynxPercentAdjust(r));
                                this.lynxPercentAdjustments.sort((a, b) => a.id - b.id);
                                this.percentAdjustmentsRetrieved.next(this.lynxPercentAdjustments);
                                break;
                            case 'change-pct-adjust-duration':
                                const durs = payload.content.result;
                                this.logger.log(durs?.length, durs?.length);
                                break;
                            case 'change-pct-adjust':
                                // TODO: Handle change=pct-adjust response if necessary.
                                // const result = payload.content?.result;

                                // We don't need to do this here. It will be done in the loop where we actually make the changes. This is redundant and costly.
                                // if (this.spatialAdjustCourseItem != null) {
                                //     // Request Spatial Adjust Data from Lynx Cloud to get updated Current Adjustment
                                //     // This will validate that our adjustment was successful or not.
                                //     this.requestSpatialAdjustData(this.spatialAdjustCourseItem);
                                // }
                                break;
                            default:
                                break;
                        }
                    }
                });

        })
    }

    private parseGenericMessage(payload: string): GenericMqttMessage {
        const payloadPart = payload.split('\n');

        return new GenericMqttMessage({
            subject: payloadPart[0].replace('subject: ', ''),
            messageId: payloadPart[1].replace('message-id: ', ''),
            clientId: payloadPart[2].replace('client-id: ', ''),
            contentType: payloadPart[3].replace('content-type: ', ''),
            content: payloadPart[4].replace('content: ', '')
        })
    }
}
